import { createSlice } from '@reduxjs/toolkit';
import { AppThunk, RootState } from '../store';
import axios from '../../resources/axios';
import { handleDefaultErrors } from '../../resources/functions';

export interface ModalAlertData {
  _id?: string;
  city?: string;
  enabled?: boolean;
  photo: string;
  mainText: string;
  description: string;
  description2: string;
  createdAt?: string;
  updatedAt?: string;
}

interface ModalAlertState {
  items: ModalAlertData[];
  loading: boolean;
  shouldUpdate: boolean;
}

const initialState: ModalAlertState = {
  items: [],
  loading: false,
  shouldUpdate: true
};

const mobileAppModalAlertSlice = createSlice({
  name: 'mobileAppModalAlert',
  initialState,
  reducers: {
    setModalAlertLoading: (state, action) => {
      state.loading = action.payload;
    },
    setModalAlerts: (state, action) => {
      state.items = action.payload;
    },
    setShouldUpdateModalAlert: (state, action) => {
      state.shouldUpdate = action.payload;
    }
  }
});

export const {
  setModalAlertLoading,
  setModalAlerts,
  setShouldUpdateModalAlert
} = mobileAppModalAlertSlice.actions;

export const refreshModalAlertsAsync = (): AppThunk => async dispatch => {
  try {
    const response = await axios.get('/modal/admin');
    dispatch(setModalAlerts(response.data.data));
  } catch (error) {
    handleDefaultErrors(error);
  }
};

export const getModalAlertsAsync = (): AppThunk => async dispatch => {
  try {
    dispatch(setModalAlertLoading(true));
    await dispatch(refreshModalAlertsAsync());
    dispatch(setModalAlertLoading(false));
  } catch (error) {
    handleDefaultErrors(error);
  }
};

export const deleteModalAlertAsync =
  (id: string, success?: () => void): AppThunk =>
    async dispatch => {
      try {
        await axios.delete(`/modal/${id}`);
        if (success) success();
        dispatch(refreshModalAlertsAsync());
      } catch (error) {
        handleDefaultErrors(error);
      }
    };

export const updateModalAlertAsync =
  (
    data: ModalAlertData,
    file?: string | File,
    success?: () => void
  ): AppThunk =>
    async dispatch => {
      try {
        const updatedData = { ...data };

        if (file instanceof File) {
          const formData = new FormData();
          formData.append('file', file);
          const response = await axios.post(`/files/persistent`, formData);
          updatedData.photo = response.data.path;
        }

        await axios.put(`/modal/${data._id}`, updatedData);

        if (success) success();
        dispatch(refreshModalAlertsAsync());
      } catch (error) {
        handleDefaultErrors(error);
      }
    };

export const createModalAlertAsync =
  (
    data: ModalAlertData,
    file?: string | File,
    success?: () => void
  ): AppThunk =>
    async dispatch => {
      try {
        const newData = { ...data };

        if (file instanceof File) {
          const formData = new FormData();
          formData.append('file', file);
          const response = await axios.post(`/files/persistent`, formData);
          newData.photo = response.data.path;
        }

        await axios.post('/modal', newData);

        if (success) success();
        dispatch(refreshModalAlertsAsync());
      } catch (error) {
        handleDefaultErrors(error);
      }
    };

export const selectModalAlerts = (state: RootState) => state.mobileAppModalAlert;

export default mobileAppModalAlertSlice.reducer;
